import { getWithAuth, postWithAuthLocal, deleteWithAuth } from './api/client';
import apiVariables from './api/apiVariables';
import { getSensitiveData } from '../utils/secureStorage';
import type {
  SkillActionResponse,
  SkillApprovalActionPayload,
  SkillApprovalPagedResult,
  SkillApprovalQuery,
  SkillApprovalResponse,
  SkillApprovalRow,
  SkillApprovalRejectPayload,
} from '../types/admin';

const normalizeRow = (item: any): SkillApprovalRow => ({
  userSkillId: item.userSkillId,
  userId: item.userId || '',
  employeeName: item.employeeName || 'Employee',
  email: item.email ?? null,
  skillId: item.skillId,
  skillName: item.skillName || '',
  experience: Number(item.experience) || 0,
  submittedDate: item.submittedDate || '',
  approvalStatusId: item.approvalStatusId,
  approvalStatus: item.approvalStatus || 'Pending',
  approvedBy: item.approvedBy ?? null,
  approvedByName: item.approvedByName ?? null,
  approvedDate: item.approvedDate ?? null,
  rejectionReason: item.rejectionReason ?? null,
});

const normalizePagedResponse = (
  response: SkillApprovalResponse | SkillApprovalRow[]
): SkillApprovalPagedResult => {
  if (Array.isArray(response)) {
    const data = response.map(normalizeRow);
    return { data, recordsTotal: data.length, recordsFiltered: data.length };
  }

  const responseData = response?.data;

  if (Array.isArray(responseData)) {
    const data = responseData.map(normalizeRow);
    return {
      data,
      recordsTotal: response.recordsTotal ?? data.length,
      recordsFiltered: response.recordsFiltered ?? data.length,
    };
  }

  if (responseData && Array.isArray((responseData as SkillApprovalPagedResult).data)) {
    const paged = responseData as SkillApprovalPagedResult;
    const data = paged.data.map(normalizeRow);
    return {
      data,
      recordsTotal: paged.recordsTotal ?? response.recordsTotal ?? data.length,
      recordsFiltered: paged.recordsFiltered ?? response.recordsFiltered ?? data.length,
    };
  }

  throw new Error('The skill approvals API returned an unexpected response.');
};

export const getPendingSkills = async (
  query: SkillApprovalQuery
): Promise<SkillApprovalPagedResult> => {
  const response = await getWithAuth<SkillApprovalResponse | SkillApprovalRow[]>(
    apiVariables.SkillApprovalList,
    {
      pageNumber: query.pageNumber,
      pageSize: query.pageSize,
      search: query.search,
      orderColumn: query.sortColumn,
      orderDirection: query.sortDirection,
      approvalStatus: query.filters.approvalStatus || undefined,
      submittedFrom: query.filters.submittedFrom || undefined,
      submittedTo: query.filters.submittedTo || undefined,
    }
  );

  return normalizePagedResponse(response);
};

export const getPendingSkillsCount = async () => {
  const response = await getPendingSkills({
    pageNumber: 1,
    pageSize: 1,
    search: '',
    sortColumn: 'SubmittedDate',
    sortDirection: 'Descending',
    filters: { approvalStatus: 'Pending', submittedFrom: '', submittedTo: '' },
  });

  return response.recordsFiltered;
};

export const approveSkill = (payload: SkillApprovalActionPayload) =>
  postWithAuthLocal<SkillActionResponse>(apiVariables.SkillApprovalApprove, payload);

export const rejectSkill = (payload: SkillApprovalRejectPayload) =>
  postWithAuthLocal<SkillActionResponse>(apiVariables.SkillApprovalReject, payload);

export const deleteSkill = async (userSkillId: number) => {
  const token = await getSensitiveData('authToken');
  if (!token) throw new Error('Authorization token missing');
  return deleteWithAuth<SkillActionResponse>(`${apiVariables.SkillApprovalList}/${userSkillId}`, token);
};
